"use client"

import { useEffect } from "react"
import { useAuth } from "@clerk/nextjs"
import { isIOSDevice, isInstalledPWA } from "@/lib/push-notifications"
import { trackWizardCompletion } from "@/lib/ios-install-wizard"

/**
 * Background component that passively detects PWA installation
 * Runs on every page load and window focus event
 * Automatically updates wizard state when installation is detected
 */
export function IOSInstallDetector() {
  const { userId } = useAuth()

  useEffect(() => {
    // Only run on iOS devices
    if (!isIOSDevice()) return
    if (!userId) return

    // Check on mount
    checkAndUpdateInstallStatus()

    // Check on window focus (user returns to app)
    const handleFocus = () => {
      checkAndUpdateInstallStatus()
    }

    window.addEventListener("focus", handleFocus)

    // Periodic check every 30 seconds (passive detection)
    const intervalId = setInterval(checkAndUpdateInstallStatus, 30000)

    return () => {
      window.removeEventListener("focus", handleFocus)
      clearInterval(intervalId)
    }
  }, [userId])

  async function checkAndUpdateInstallStatus() {
    if (!userId) return

    // Check if PWA is installed
    const installed = isInstalledPWA()

    if (installed) {
      console.log("[Detector] PWA installation detected")

      try {
        // Update localStorage
        trackWizardCompletion()

        // Check current wizard state in DB
        const response = await fetch("/api/user/preferences")
        if (response.ok) {
          const prefs = await response.json()

          // Only update if not already marked as completed
          if (!prefs.iosInstallWizardCompleted) {
            console.log("[Detector] Updating wizard completion in database")

            await fetch("/api/user/preferences", {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                iosInstallWizardCompleted: true,
              }),
            })
          }
        }
      } catch (error) {
        console.error("[Detector] Failed to update wizard state:", error)
      }
    }
  }

  // This component doesn't render anything
  return null
}
